import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import { authAPI } from '../api';
import LanguageSelector from '../components/LanguageSelector';

const COUNTRIES = [
  { code: 'CN', name: '中国', flag: '🇨🇳' },
  { code: 'JP', name: '日本', flag: '🇯🇵' },
  { code: 'KR', name: '韩国', flag: '🇰🇷' },
  { code: 'US', name: '美国', flag: '🇺🇸' },
  { code: 'GB', name: '英国', flag: '🇬🇧' },
  { code: 'FR', name: '法国', flag: '🇫🇷' },
  { code: 'DE', name: '德国', flag: '🇩🇪' },
  { code: 'IT', name: '意大利', flag: '🇮🇹' },
  { code: 'ES', name: '西班牙', flag: '🇪🇸' },
  { code: 'RU', name: '俄罗斯', flag: '🇷🇺' },
  { code: 'BR', name: '巴西', flag: '🇧🇷' },
  { code: 'AU', name: '澳大利亚', flag: '🇦🇺' },
  { code: 'CA', name: '加拿大', flag: '🇨🇦' },
  { code: 'SG', name: '新加坡', flag: '🇸🇬' },
  { code: 'TH', name: '泰国', flag: '🇹🇭' },
  { code: 'VN', name: '越南', flag: '🇻🇳' },
  { code: 'IN', name: '印度', flag: '🇮🇳' },
  { code: 'MY', name: '马来西亚', flag: '🇲🇾' },
];

export default function RegisterPage() {
  const { t } = useI18n();
  const [form, setForm] = useState({
    username: '', email: '', password: '', confirmPassword: '',
    country_code: '', preferred_language: (function(){var l=(navigator.language||navigator.userLanguage||'').toLowerCase();if(l.startsWith('zh'))return'zh';if(l.startsWith('ja'))return'ja';if(l.startsWith('ko'))return'ko';if(l.startsWith('fr'))return'fr';if(l.startsWith('de'))return'de';if(l.startsWith('it'))return'it';if(l.startsWith('ru'))return'ru';if(l.startsWith('es'))return'es';if(l.startsWith('pt'))return'pt';return'en';})(),
    age_confirmed: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirmPassword) return setError(t('pwdMismatch'));
    if (form.password.length < 6) return setError(t('registerWeakPwd'));
    if (!form.age_confirmed) return setError(t('adultText'));
    setLoading(true);
    try {
      const res = await authAPI.register({
        username: form.username, email: form.email, password: form.password,
        country_code: form.country_code, preferred_language: form.preferred_language, age_confirmed: form.age_confirmed,
      });
      login(res.data.user, res.data.token);
      fetch('/api/analytics/event',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({event:'register_complete',path:window.location.pathname})}).catch(()=>{});
      navigate('/profile/' + res.data.user.id + '?onboarding=1');
    } catch (err) {
      setError(err.response?.data?.error || t('registerFail'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-center">
      <div className="auth-card">
        <h1 className="auth-title">{t('registerTitle')}</h1>
        <p className="auth-subtitle">{t('registerSubtitle')}</p>
        <form onSubmit={handleSubmit}>
          {error && <div className="alert alert-error">{error}</div>}
          <div className="field">
            <label>{t('username')}</label>
            <input autoComplete="username" value={form.username} onChange={(e) => update('username', e.target.value)} placeholder={t('usernamePh')} required />
          </div>
          <div className="field">
            <label>{t('email')}</label>
            <input type="email" autoComplete="email" inputMode="email" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="your@email.com" required />
          </div>
          <div className="field">
            <label>{t('password')}</label>
            <div style={{position:'relative'}}><input type={showPassword ? 'text' : 'password'} autoComplete="new-password" value={form.password} onChange={(e) => update('password', e.target.value)} placeholder={t('registerWeakPwd')} required style={{paddingRight:70}} /><button type="button" onClick={() => setShowPassword(v => !v)} style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',border:0,background:'transparent',color:'var(--text-dim)',cursor:'pointer',fontSize:12}}>{showPassword ? '隐藏' : '显示'}</button></div>
          </div>
          <div className="field">
            <label>{t('confirmPassword')}</label>
            <input type={showPassword ? 'text' : 'password'} autoComplete="new-password" value={form.confirmPassword} onChange={(e) => update('confirmPassword', e.target.value)} placeholder={t('confirmPwdPh')} required />
          </div>
          <div className="field">
            <label>{t('country')}</label>
            <select value={form.country_code} onChange={(e) => update('country_code', e.target.value)} required>
              <option value="">{t('countryPh')}</option>
              {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.flag} {c.name}</option>)}
            </select>
          </div>
          <LanguageSelector value={form.preferred_language} onChange={(v) => update('preferred_language', v)} label={t('language')} />
          <label style={{display:'flex',alignItems:'flex-start',gap:8,fontSize:'0.8rem',color:'var(--text-dim)',margin:'14px 0'}}><input type="checkbox" checked={form.age_confirmed} onChange={(e)=>update('age_confirmed',e.target.checked)} style={{marginTop:3}} />{t('adultText')}</label>
          <button className="btn btn-primary btn-block" type="submit" disabled={loading}>
            {loading ? t('registerLoading') : '🌍 ' + t('registerBtn')}
          </button>
        </form>
        <p className="auth-footer">
          {t('loginHere')} <Link to="/login">{t('loginBtn')} →</Link>
        </p>
      </div>
    </div>
  );
}
